import { garantirPerto } from './FaixaOficio';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type TailorMenu } from '../api/types';
import { playAlfaiataria } from '../audio/mixer';
import { prefetchIcons } from '../assets/items';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { CraftBar } from './CraftBar';
import { ItemIcon } from './ItemIcon';
import { Linha } from './kit';

interface Props {
  active: boolean;
  tick: number;
  onResultado(ev: { evento: 'costurou' | 'cortou'; nome?: string; n?: number }): void;
}

/** Cobre alfaiataria.* no validar-chaves: os códigos chegam pelo `code` HTTP. */
const chaveAlfaiataria = (x: string) => t(`alfaiataria.${x}`);

export function TailorGump({ active, tick, onResultado }: Props) {
  const [menu, setMenu] = useState<TailorMenu | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preparo, setPreparo] = useState<{ ate: number; ms: number } | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current != null) window.clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (!active) return;
    let live = true;
    void api.tailorMenu()
      .then(next => {
        if (!live) return;
        setMenu(next);
        setError(null);
        prefetchIcons([
          ...next.receitas.map(r => r.defId),
          ...next.receitas.map(r => r.material),
          'sewing-kit'
        ]);
      })
      .catch(err => {
        if (!live) return;
        setError(err instanceof ApiError ? err.message : t('alfaiataria.naoCosturou'));
      });
    return () => { live = false; };
  }, [active, tick]);

  const costurar = async (defId: string, rende: number) => {
    setBusy(true);
    setError(null);
    try {
      const perto = await garantirPerto('alfaiataria');
      if (!perto.ok) { setBusy(false); setError(perto.motivo ?? null); return; }
      playAlfaiataria();
      const res = await api.sew(defId);
      const peca = res.pack.items.find(i => i.defId === defId);
      const nome = peca ? nomeDoItem(peca.defId, peca.name) : undefined;
      setPreparo({ ate: performance.now() + res.prontoEmMs, ms: res.prontoEmMs });
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setPreparo(null);
        setBusy(false);
        if (defId === 'bandage') onResultado({ evento: 'cortou', n: rende });
        else onResultado({ evento: 'costurou', nome });
      }, res.prontoEmMs);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError) {
        setError(err.message);
        const x = (err.code ?? '').startsWith('alfaiataria.') ? err.code!.slice('alfaiataria.'.length) : '';
        if (x) void chaveAlfaiataria(x);
      } else {
        setError(t('alfaiataria.naoCosturou'));
      }
    }
  };

  const semKit = menu !== null && !menu.temKit;

  return (
    <>
      {preparo && <CraftBar ateMs={preparo.ate} duracaoMs={preparo.ms} rotulo={t('alfaiataria.costurando')} />}
      {error && <p className="notice" role="alert">{error}</p>}
      {semKit && <p className="notice" role="alert">{t('alfaiataria.semKit')}</p>}
      <p className="smith-skill alchemy-head">
        {t('alfaiataria.suaPericia', { n: pericia(menu?.alfaiataria ?? 0) })}
        <span>{t('alfaiataria.kit')} {menu?.temKit ? '✔' : '-'}</span>
        <span>{t('alfaiataria.pano', { pano: numero(menu?.pano ?? 0) })}</span>
      </p>
      <ul className="pack-list smith-list tailor-list" aria-label={t('alfaiataria.titulo')}>
        {!menu && <li className="empty">{t('comum.montando')}</li>}
        {menu?.receitas.map(linha => {
          const estado = estadoLinha(linha, semKit);
          const nome = nomeDoItem(linha.defId, linha.name);
          const material = nomeDoItem(linha.material, linha.materialName);
          return (
            <li key={linha.defId} className={estado.travado ? 'is-lock' : undefined}>
              <Linha
                lead={<ItemIcon id={linha.defId} label={nome} size={36} eager />}
                trail={(
                  <button
                    type="button"
                    className="btn btn--tiny"
                    disabled={busy || estado.desabilitado}
                    title={estado.titulo}
                    onClick={() => void costurar(linha.defId, linha.rende)}
                  >
                    {estado.rotulo}
                  </button>
                )}
              >
                {nome}
                <span className="smith-need">{t('alfaiataria.custo', { n: linha.qtd, material, skill: pericia(linha.skill) })}</span>
              </Linha>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function pericia(n: number): string {
  if (Number.isInteger(n)) return numero(n);
  return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function estadoLinha(
  linha: TailorMenu['receitas'][number],
  semKit: boolean
): { travado: boolean; desabilitado: boolean; rotulo: string; titulo?: string } {
  if (semKit)
    return { travado: false, desabilitado: true, rotulo: t('alfaiataria.costurar'), titulo: t('alfaiataria.semKit') };
  if (!linha.podeSkill)
    return { travado: true, desabilitado: true, rotulo: `🔒 ${pericia(linha.skill)}`, titulo: t('alfaiataria.periciaBaixa', { n: pericia(linha.skill) }) };
  const material = nomeDoItem(linha.material, linha.materialName);
  if (linha.tenho < linha.qtd) {
    const falta = linha.qtd - linha.tenho;
    return { travado: false, desabilitado: true, rotulo: t('alfaiataria.faltam', { n: falta, material }) };
  }
  const pronto = t('alfaiataria.pronto', { n: linha.qtd, material });
  return {
    travado: false,
    desabilitado: false,
    rotulo: linha.rende > 1 ? `${pronto} → ${numero(linha.rende)}` : pronto
  };
}
